//---------------------------------------------------------
// HA pair API callers (contract 0012 section 3). Save needs If-Match and
// Idempotency-Key, which the generic fetcher can't send, so these use fetch
// with the same base URL and bearer token.
//---------------------------------------------------------
import {get_local_storage} from 'common';
import type {HAApiError, HAPairDetail, HAPairListItem, HAPowerPreflight, HASaveResult, HASettingsDoc, HANodeIndex} from 'types/haPair';

export interface HAResult<T = unknown> {
	status: number;
	data: T | null;
	error?: HAApiError;
}

async function haFetch<T>(method: string, path: string, body?: unknown, headers?: Record<string, string>): Promise<HAResult<T>> {
	const {getApiBaseUrl} = await import('utils/apiProxy');
	const token = get_local_storage('access_token') ?? get_local_storage('token');
	const h: Record<string, string> = {
		Accept: 'application/json',
		...(body !== undefined ? {'Content-Type': 'application/json'} : {}),
		...(token ? {Authorization: `Bearer ${token}`} : {}),
		...(headers || {}),
	};
	try {
		const r = await fetch(`${getApiBaseUrl()}${path}`, {method, headers: h, body: body !== undefined ? JSON.stringify(body) : undefined});
		const data = r.status === 204 ? null : await r.json().catch(() => null);
		const err = data && typeof data === 'object' && 'error' in data && typeof (data as any).error === 'object' ? ((data as any).error as HAApiError) : undefined;
		return {status: r.status, data: data as T, error: r.ok ? undefined : err || {code: `HTTP_${r.status}`, message: `HTTP ${r.status}`}};
	} catch (e: any) {
		return {status: 0, data: null, error: {code: 'NETWORK', message: e?.message || 'network error'}};
	}
}

/** Pair items from GET /oam/loxilbs (kind "ha_pair"). */
export function pairItemsFromList(list: unknown): HAPairListItem[] {
	if (!Array.isArray(list)) return [];
	return list.filter((x: any) => x && x.kind === 'ha_pair') as HAPairListItem[];
}

export async function getPairList(): Promise<HAPairListItem[]> {
	const r = await haFetch<unknown[]>('GET', '/loxilbs');
	return r.status === 200 ? pairItemsFromList(r.data) : [];
}

export async function getPair(pairId: string): Promise<HAResult<HAPairDetail>> {
	return haFetch<HAPairDetail>('GET', `/pairs/${encodeURIComponent(pairId)}`);
}

export async function getPairSettings(pairId: string): Promise<HAResult<{settingsRev: number; settings: HASettingsDoc}>> {
	return haFetch('GET', `/pairs/${encodeURIComponent(pairId)}/settings`);
}

export async function putPairSettings(pairId: string, rev: number, settings: HASettingsDoc, idempotencyKey: string): Promise<HASaveResult> {
	const r = await haFetch<any>('PUT', `/pairs/${encodeURIComponent(pairId)}/settings`, {settings}, {
		'If-Match': `"${rev}"`,
		'Idempotency-Key': idempotencyKey,
	});
	const d = r.data || {};
	return {status: r.status, settingsRev: d.settingsRev, result: d.result, nodes: d.nodes, error: r.status === 200 ? undefined : d.error || r.error, node: d.node};
}

export async function resyncPairNode(pairId: string, node: HANodeIndex): Promise<HAResult<{node: number; syncState: string}>> {
	return haFetch('POST', `/pairs/${encodeURIComponent(pairId)}/nodes/${node}/resync`);
}

export async function getNodePower(pairId: string, node: HANodeIndex): Promise<HAResult<HAPowerPreflight>> {
	return haFetch('GET', `/pairs/${encodeURIComponent(pairId)}/nodes/${node}/power`);
}

export async function postNodePower(pairId: string, node: HANodeIndex, action: 'boot' | 'shutdown' | 'reboot', confirmAllTrafficStops: boolean) {
	return haFetch<any>('POST', `/pairs/${encodeURIComponent(pairId)}/nodes/${node}/power/${action}`, confirmAllTrafficStops ? {confirmAllTrafficStops: true} : {});
}

export async function deletePair(pairId: string): Promise<HAResult<{survivor?: {node: number; linodeId?: number}}>> {
	return haFetch('POST', `/pairs/${encodeURIComponent(pairId)}/deprovision`);
}
